
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Share, Check } from 'lucide-react';
import { toast } from 'sonner';

interface InviteFriendsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InviteFriendsModal: React.FC<InviteFriendsModalProps> = ({
  isOpen,
  onClose
}) => {
  const { addPoints } = useAuth();
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [invitedEmails, setInvitedEmails] = useState<string[]>([]);
  
  const handleInvite = () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (invitedEmails.includes(email)) {
      toast.error('You have already invited this email');
      return;
    }
    
    setIsSending(true);
    
    // Mock sending invitation
    setTimeout(() => {
      // Add to invited list
      setInvitedEmails([...invitedEmails, email]);
      
      // Award points (100 per invitation)
      addPoints(100);
      
      // Success message
      toast.success(`Invitation sent to ${email}! You earned 100 points.`);
      
      // Reset form
      setEmail('');
      setIsSending(false);
    }, 1000);
  };
  
  const inviteLink = "snapchallenge.app/invite/1234";
  
  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copied to clipboard!');
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-bauhaus-offwhite border-2 border-bauhaus-green">
        <DialogHeader>
          <DialogTitle className="text-bauhaus-darkgreen">Invite Friends</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          <div className="bg-bauhaus-cream p-3 rounded-md text-sm text-bauhaus-darkgreen">
            <p>Invite friends to join SnapChallenge and earn 100 points for each friend who signs up!</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-bauhaus-darkgreen">Email</Label>
            <div className="flex space-x-2">
              <Input 
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="friend@example.com"
                type="email"
                className="border-bauhaus-darkgreen/50 focus:border-bauhaus-darkgreen"
              />
              <Button 
                onClick={handleInvite} 
                disabled={isSending}
                className="shrink-0 bg-bauhaus-green text-bauhaus-offwhite hover:bg-bauhaus-green/90"
              >
                {isSending ? 'Sending...' : 'Invite'}
              </Button>
            </div>
          </div>
          
          {invitedEmails.length > 0 && (
            <div className="space-y-2">
              <Label className="text-bauhaus-darkgreen">Invited Friends</Label>
              <div className="bg-bauhaus-cream rounded-md p-2">
                {invitedEmails.map((invitedEmail, index) => (
                  <div key={index} className="flex items-center text-sm py-1">
                    <Check className="h-4 w-4 text-green-500 mr-2" />
                    <span className="text-bauhaus-darkgreen">{invitedEmail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="border-t border-bauhaus-green/20 pt-4">
            <Label className="text-bauhaus-darkgreen">Share Invite Link</Label>
            <div className="flex mt-2 w-full">
              <Input 
                value={inviteLink} 
                readOnly
                className="rounded-r-none border-bauhaus-darkgreen/50"
              />
              <Button 
                onClick={copyInviteLink}
                className="rounded-l-none shrink-0 bg-bauhaus-green hover:bg-bauhaus-green/90 text-bauhaus-offwhite"
              >
                <Share className="h-4 w-4 mr-1" />
                Copy
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
